/**
 * Attachments of the open composer (DESIGN.md T11): uploaded files, the original's attachments on
 * forward (removable chips) and attached mailbox items (.eml). Files are uploaded as soon as they
 * are added (chip with progress); sending only references their ids.
 *
 * The limits (per file, total, count) come from the platform API and are checked here first, so the
 * user gets the explanation before anything is uploaded; the server checks again. Which files are
 * allowed at all (type rules, malware) only the server knows: it scans every upload with gxscan, and
 * its refusal shows on the chip.
 */
import { useI18n } from '@platform/i18n';
import { errorCode, formatBytes, toast, useAsync, useErrorText, type ComposerAttachment } from '@platform/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type AttachmentLimits } from './api';
import type { DraftSaved } from '@platform/api-client';

/** One attachment of the composer. */
export interface DraftAttachment {
  /** Chip id (local). */
  id: string;
  /** upload = file added in the composer, original = attachment of the forwarded mail, item = mailbox item as .eml. */
  source: 'upload' | 'original' | 'item' | 'draft';
  name: string;
  /** file or attached message (chip icon). */
  kind: 'file' | 'message';
  size?: number | null | undefined;
  /** 0..1 while uploading. */
  progress?: number | undefined;
  error?: string | undefined;
  /** Server id: upload id, original attachment id or mailbox item id. */
  ref?: string | undefined;
}

/** Server refusals of the scan step (gxscan: rules, malware, scanner down) -> text key (packages/i18n). */
const SCAN_REFUSALS: Readonly<Record<string, 'webmail.compose.fileBlocked' | 'webmail.compose.fileInfected' | 'webmail.compose.fileScanUnavailable'>> = {
  attachment_blocked: 'webmail.compose.fileBlocked',
  attachment_infected: 'webmail.compose.fileInfected',
  attachment_scan_unavailable: 'webmail.compose.fileScanUnavailable',
};

export interface ComposeAttachments {
  items: readonly DraftAttachment[];
  /** Chips for the composer (sizes formatted). */
  chips: readonly ComposerAttachment[];
  addFiles: (files: File[]) => void;
  /** Adds a mailbox item as .eml (drag from the list), once. */
  addItem: (item: { id: string; name: string }) => void;
  remove: (id: string) => void;
  /** Starts over with these attachments (new composer). */
  reset: (initial: readonly DraftAttachment[]) => void;
  /** Uploads still running or failed (sending must wait or the user removes them). */
  blocked: boolean;
  /** Changed since the composer opened. */
  changed: boolean;
  change: number;
  uploading: boolean;
  adopt: (sentParts: readonly DraftAttachment[], saved: DraftSaved['attachments']) => void;
  refuse: (source: string, ref: string, message: string) => void;
  ids: { uploadIds: string[]; originalAttachmentIds: string[]; attachItemIds: string[]; draftAttachmentIds: string[] };
}

export function useComposeAttachments(enabled = true): ComposeAttachments {
  const { t, formatNumber } = useI18n();
  const errorText = useErrorText();
  const limits = useAsync(() => api.attachmentLimits(), 0, enabled);
  const [items, setItems] = useState<readonly DraftAttachment[]>([]);
  const [changed, setChanged] = useState(false);
  const [change, setChange] = useState(0);
  const controllers = useRef(new Map<string, AbortController>());
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  });

  const size = useCallback((bytes: number) => formatBytes(bytes, formatNumber), [formatNumber]);
  const updateItems = (change: (list: readonly DraftAttachment[]) => readonly DraftAttachment[]) => {
    const next = change(itemsRef.current); itemsRef.current = next; setItems(next);
  };
  const patch = (id: string, change: Partial<DraftAttachment>) => updateItems((list) => list.map((item) => (item.id === id ? { ...item, ...change } : item)));

  /** Localized reason a file is refused before uploading, or undefined. */
  const refusal = (file: File, limit: AttachmentLimits, accepted: readonly DraftAttachment[]): string | undefined => {
    const uploads = accepted.filter((item) => item.source === 'upload' && !item.error);
    if (file.size > limit.maxFileBytes) return t('webmail.compose.fileTooLarge', { name: file.name, max: size(limit.maxFileBytes) });
    if (uploads.length >= limit.maxCount) return t('webmail.compose.tooManyFiles', { name: file.name, max: limit.maxCount });
    const total = uploads.reduce((sum, item) => sum + (item.size ?? 0), 0);
    if (total + file.size > limit.maxTotalBytes) return t('webmail.compose.totalTooLarge', { name: file.name, max: size(limit.maxTotalBytes) });
    return undefined;
  };

  /** The server's refusal in the user's language (limits as configured there). */
  const uploadError = (error: unknown, name: string): string => {
    const limit = limits.data;
    const code = errorCode(error);
    const scanRefusal = code === undefined ? undefined : SCAN_REFUSALS[code];
    if (scanRefusal) return t(scanRefusal, { name });
    if (limit && code === 'attachment_too_large') return t('webmail.compose.fileTooLarge', { name, max: size(limit.maxFileBytes) });
    if (limit && code === 'attachments_total_too_large') return t('webmail.compose.totalTooLarge', { name, max: size(limit.maxTotalBytes) });
    if (limit && code === 'too_many_attachments') return t('webmail.compose.tooManyFiles', { name, max: limit.maxCount });
    return `${t('webmail.compose.uploadFailed', { name })} ${errorText.describe(error)}`;
  };

  const upload = (item: DraftAttachment, file: File) => {
    const controller = new AbortController();
    controllers.current.set(item.id, controller);
    api
      .uploadAttachment(file, { signal: controller.signal, onProgress: (fraction) => patch(item.id, { progress: fraction }) })
      .then(
        (uploaded) => patch(item.id, { progress: undefined, ref: uploaded.id, name: uploaded.name, size: uploaded.size }),
        (error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          const message = uploadError(error, file.name);
          patch(item.id, { progress: undefined, error: message });
          toast.error(message);
        },
      )
      .finally(() => controllers.current.delete(item.id));
  };

  const addFiles = (files: File[]) => {
    const accepted: DraftAttachment[] = [];
    for (const file of files) {
      const reason = limits.data ? refusal(file, limits.data, [...itemsRef.current, ...accepted]) : undefined;
      if (reason) {
        toast.error(reason);
        continue;
      }
      const item: DraftAttachment = { id: crypto.randomUUID(), source: 'upload', name: file.name || 'attachment', kind: 'file', size: file.size, progress: 0 };
      accepted.push(item);
      upload(item, file);
    }
    if (accepted.length === 0) return;
    updateItems((list) => [...list, ...accepted]);
    setChanged(true);
    setChange(value => value + 1);
  };

  const addItem = ({ id, name }: { id: string; name: string }) => {
    if (itemsRef.current.some((item) => item.source === 'item' && item.ref === id)) return;
    updateItems((list) => [...list, { id: `item-${id}`, source: 'item', kind: 'message', name, ref: id }]);
    setChanged(true);
    setChange(value => value + 1);
  };

  const remove = (id: string) => {
    const item = itemsRef.current.find((candidate) => candidate.id === id);
    controllers.current.get(id)?.abort();
    // Uploaded files are removed from the server right away (the sweep would get them later too).
    if (item?.source === 'upload' && item.ref) api.deleteUpload(item.ref).catch(() => undefined);
    updateItems((list) => list.filter((candidate) => candidate.id !== id));
    setChanged(true);
    setChange(value => value + 1);
  };

  const reset = useCallback((initial: readonly DraftAttachment[]) => {
    for (const controller of controllers.current.values()) controller.abort();
    controllers.current.clear();
    itemsRef.current = initial;
    setItems(initial);
    setChanged(false);
  }, []);

  const chips = useMemo<ComposerAttachment[]>(
    () => items.map((item) => ({ id: item.id, name: item.name, kind: item.kind, sizeLabel: item.size == null ? undefined : size(item.size), progress: item.progress, error: item.error })),
    [items, size],
  );

  const refs = (source: DraftAttachment['source']) => itemsRef.current.filter((item) => item.source === source && item.ref && !item.error).map((item) => item.ref!);
  return {
    get items() { return itemsRef.current; },
    chips,
    addFiles,
    addItem,
    remove,
    reset,
    get blocked() { return itemsRef.current.some((item) => item.progress !== undefined || item.error !== undefined); },
    changed,
    change,
    get uploading() { return itemsRef.current.some(item => item.progress !== undefined); },
    adopt: (sentParts, saved) => updateItems(list => list.map(item => {
      const sent = sentParts.find(part => part.id === item.id);
      if (!sent) return item; // A new chip added while saving was not part of this response.
      const stored = saved.find(part => part.sourceRef?.source === sent.source && part.sourceRef.ref === sent.ref);
      if (!stored) return { ...item, error: t('webmail.draft.notSaved') }; // Fail closed; filenames are never identities.
      return { id: item.id, source: 'draft' as const, ref: stored.id, name: stored.name, kind: stored.kind === 'item' ? 'message' as const : 'file' as const, size: stored.size };
    })),
    refuse: (source, ref, message) => updateItems(list => list.map(item => item.source === source && item.ref === ref ? { ...item, error: message } : item)),
    get ids() { return { uploadIds: refs('upload'), originalAttachmentIds: refs('original'), attachItemIds: refs('item'), draftAttachmentIds: refs('draft') }; },
  };
}
